import { db } from '../db/indexeddb'

/**
 * ID Resolution Helpers
 * Resolve between local Dexie UUIDs and Supabase cloud UUIDs.
 */

// ─── Tag ID Resolution ──────────────────────────────────────────────────────

/**
 * Resolve a local tagId (Dexie UUID) to its Supabase cloud UUID.
 * Returns null if the tag doesn't exist or hasn't been synced yet.
 */
export async function resolveCloudTagId(localTagId: string | null | undefined): Promise<string | null> {
  if (!localTagId) return null
  const tag = await db.getTag(localTagId)
  return tag?.cloudId ?? null
}

/**
 * Resolve a cloud tag id (Supabase UUID) to its local Dexie tag id.
 * First checks if the cloudTagId IS a local Dexie ID (unified identity),
 * then falls back to cloudId matching (legacy).
 */
export async function resolveLocalTagId(cloudTagId: string | null | undefined): Promise<string | null> {
  if (!cloudTagId) return null
  const directMatch = await db.tags.get(cloudTagId)
  if (directMatch) return directMatch.id
  const match = await db.tags.filter(t => t.cloudId === cloudTagId).first()
  return match?.id ?? null
}

/**
 * Build a mapping of local tag IDs → cloud tag IDs for bulk resolution.
 * Used when pushing tasks with tags to cloud.
 */
export async function buildLocalToCloudTagIdMap(): Promise<Record<string, string>> {
  const tags = await db.tags.filter(t => !!t.cloudId).toArray()
  const map: Record<string, string> = {}
  for (const tag of tags) {
    if (tag.cloudId) map[tag.id] = tag.cloudId
  }
  return map
}

/**
 * Build a mapping of cloud tag IDs → local tag IDs for bulk resolution.
 * Used when pulling tasks with tags from cloud.
 */
export async function buildCloudToLocalTagIdMap(): Promise<Record<string, string>> {
  const tags = await db.tags.toArray()
  const map: Record<string, string> = {}
  for (const tag of tags) {
    if (tag.cloudId) map[tag.cloudId] = tag.id
  }
  return map
}

// ─── Section ID Resolution ──────────────────────────────────────────────────

/**
 * Resolve a local sectionId (Dexie UUID) to its Supabase cloud UUID.
 * Returns null if the section doesn't exist or hasn't been synced yet.
 */
export async function resolveCloudSectionId(localSectionId: string | null | undefined): Promise<string | null> {
  if (!localSectionId) return null
  const section = await db.getSection(localSectionId)
  return section?.cloudId ?? null
}

/**
 * Resolve a cloud section_id (Supabase UUID) to its local Dexie section id.
 * First checks if the cloudSectionId IS a local Dexie ID (unified identity),
 * then falls back to cloudId matching (legacy).
 */
export async function resolveLocalSectionId(cloudSectionId: string | null | undefined): Promise<string | null> {
  if (!cloudSectionId) return null
  const directMatch = await db.sections.get(cloudSectionId)
  if (directMatch) return directMatch.id
  // Legacy fallback: search by cloudId (cloudId not indexed on sections, use filter)
  const match = await db.sections.filter(s => s.cloudId === cloudSectionId).first()
  return match?.id ?? null
}

/**
 * Resolve a local projectId (Dexie UUID) to its Supabase cloud UUID.
 * Returns null if the project doesn't exist or hasn't been synced yet.
 */
export async function resolveCloudProjectId(localProjectId: string | null | undefined): Promise<string | null> {
  if (!localProjectId) return null
  const project = await db.getProject(localProjectId)
  return project?.cloudId ?? null
}

/**
 * Resolve a cloud project_id (Supabase UUID) to its local Dexie project id.
 * First checks if the cloudProjectId IS a local Dexie ID (unified identity),
 * then falls back to cloudId matching (legacy).
 */
export async function resolveLocalProjectId(cloudProjectId: string | null | undefined): Promise<string | null> {
  if (!cloudProjectId) return null
  const directMatch = await db.projects.get(cloudProjectId)
  if (directMatch) return directMatch.id
  // Legacy fallback: search by cloudId (indexed query)
  const match = await db.projects.where('cloudId').equals(cloudProjectId).first()
  return match?.id ?? null
}

// ─── Habit ID Resolution ──────────────────────────────────────────────────────
// habit_logs reference their parent habit. On push we need the cloud habit id;
// on pull we map a remote log back to its local habit.

/** Resolve a local habitId (Dexie UUID) to its Supabase cloud UUID, or null. */
export async function resolveCloudHabitId(localHabitId: string | null | undefined): Promise<string | null> {
  if (!localHabitId) return null
  const habit = await db.habits.get(localHabitId)
  return habit?.cloudId ?? null
}

/**
 * Resolve a remote log's habit reference to a local Dexie habit id.
 * Prefers `habit_local_id` (unified identity), then the local row whose id
 * equals the cloud habit id, then a cloudId match.
 */
export async function resolveLocalHabitId(
  cloudHabitId: string | null | undefined,
  habitLocalId?: string | null,
): Promise<string | null> {
  if (habitLocalId) {
    const byLocal = await db.habits.get(habitLocalId)
    if (byLocal) return byLocal.id
  }
  if (!cloudHabitId) return null
  const directMatch = await db.habits.get(cloudHabitId)
  if (directMatch) return directMatch.id
  const match = await db.habits.where('cloudId').equals(cloudHabitId).first()
  return match?.id ?? null
}

/** Local task id → cloud task id (null when the task has not been pushed yet). */
export async function resolveCloudTaskId(localTaskId: string | null | undefined): Promise<string | null> {
  if (!localTaskId) return null
  const task = await db.tasks.get(localTaskId)
  return task?.cloudId ?? null
}

/**
 * Cloud task id → local task id. Prefers `task_local_id` from the payload,
 * which lets a subtask find its task on the device that created it before that
 * task's cloud id has come back. Mirrors {@link resolveLocalHabitId}.
 */
/**
 * Lokale Kennung eines **Bereichs** → seine Cloud-Kennung. Ein Bereich ist
 * selbst eine Zeile in `canvasNotes`; die Auflösung läuft darum über dieselbe
 * Tabelle wie das Element, das auf ihn zeigt.
 */
export async function resolveCloudCanvasNoteId(localId: string | null | undefined): Promise<string | null> {
  if (!localId) return null
  const note = await db.canvasNotes.get(localId)
  return note?.cloudId ?? null
}

/**
 * Cloud-Kennung eines **Bereichs** → lokale Kennung. Bevorzugt `area_local_id`
 * aus dem Payload, damit ein Element seinen Bereich auf dem Gerät findet, das
 * ihn angelegt hat, noch bevor dessen Cloud-Kennung zurück ist. Spiegelt
 * {@link resolveLocalTaskId}.
 */
export async function resolveLocalCanvasNoteId(
  cloudId: string | null | undefined,
  localIdHint?: string | null,
): Promise<string | null> {
  if (localIdHint) {
    const byLocal = await db.canvasNotes.get(localIdHint)
    if (byLocal) return byLocal.id
  }
  if (!cloudId) return null
  const directMatch = await db.canvasNotes.get(cloudId)
  if (directMatch) return directMatch.id
  const match = await db.canvasNotes.where('cloudId').equals(cloudId).first()
  return match?.id ?? null
}

export async function resolveLocalTaskId(
  cloudTaskId: string | null | undefined,
  taskLocalId?: string | null,
): Promise<string | null> {
  if (taskLocalId) {
    const byLocal = await db.tasks.get(taskLocalId)
    if (byLocal) return byLocal.id
  }
  if (!cloudTaskId) return null
  const directMatch = await db.tasks.get(cloudTaskId)
  if (directMatch) return directMatch.id
  const match = await db.tasks.where('cloudId').equals(cloudTaskId).first()
  return match?.id ?? null
}
